/* eslint-disable no-restricted-imports -- firebase compat: 新たにコードを追加する時は modular API を使う。modular API へ移行したらこのコメントごと削除する */
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
/* eslint-enable no-restricted-imports */
import { isPermissionDeniedError } from "firestore/error";
import useSWR from "swr";

import type { Event } from "@spwn/types/firebase/firestore";

export type DashboardEvent = {
  eventId: string;
  eventName: string;
  eventDateSeconds: number;
  eventImageUrl: string;
};

type DashboardEventDocument = {
  eventId: string;
  event: Pick<Event, "title" | "datetime" | "defaultImg"> | undefined;
};

/**
 * Firestoreの取得結果を、管理ダッシュボードで使うイベント情報へ変換する。
 * 存在しない・読み取りを拒否されたドキュメントは`event: undefined`として除外するため、
 * 同時に取得できた他のイベントはそのまま利用できる。
 */
const createDashboardEvents = (
  documents: readonly DashboardEventDocument[]
): DashboardEvent[] =>
  documents.flatMap(({ eventId, event }) => {
    if (!event?.title) {
      return [];
    }

    return [
      {
        eventId,
        eventName: event.title.replace(/<(\/|)br ?(\/|)>/g, "\n"),
        eventDateSeconds: event.datetime.seconds,
        eventImageUrl: event.defaultImg,
      },
    ];
  });

const GetDashboardEventsKey = "getDashboardEvents";

export const useGetDashboardEvents = (eventIds: string[]) => {
  const currentUserId = firebase.auth().currentUser?.uid ?? null;
  const uniqueEventIds = Array.from(new Set(eventIds)).sort();
  const uniqueEventIdsKey = JSON.stringify(uniqueEventIds);

  const {
    data: events = [],
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<DashboardEvent[], Error>(
    currentUserId && uniqueEventIds.length > 0
      ? [GetDashboardEventsKey, currentUserId, uniqueEventIdsKey]
      : null,
    async () => {
      const documents = await Promise.all(
        uniqueEventIds.map(async (eventId): Promise<DashboardEventDocument> => {
          try {
            const snapshot = await firebase
              .firestore()
              .collection("events")
              .doc(eventId)
              .get();
            // Firestoreは存在しないドキュメントも`exists: false`のsnapshotとして返す。
            return {
              eventId,
              event: snapshot.exists
                ? (snapshot.data() as Event | undefined)
                : undefined,
            };
          } catch (error) {
            // 公開 1 分より前のイベントと存在しないイベントは、スタッフとオーナー以外は
            // 読み取りを拒否される。1 件の拒否で全体を失敗させず、存在しないときと同じく除く
            if (isPermissionDeniedError(error)) {
              return { eventId, event: undefined };
            }
            throw error;
          }
        })
      );

      return createDashboardEvents(documents);
    }
  );

  return {
    events,
    eventNames: events.map((event) => event.eventName),
    isLoading,
    isValidating,
    error,
    mutate,
  };
};
